/**
 * useNow — period-ticking clock seeded from a baseline (typically `serverDate`
 * from `useServerTime`).
 *
 * The seed anchors the *initial* value so the first render reflects server-
 * synced time. After mount, an interval updates `now` once per `periodMs`
 * (default 60s) using client time.
 *
 * Re-seeding from `seed` only happens when the seed crosses a new period
 * bucket — this matters because callers like `useServerTime` return a fresh
 * `new Date()` each render. Depending on the raw `seed` reference would
 * reseed state every render and risk an update loop; depending on
 * `Math.floor(seed.getTime() / periodMs)` keeps the dep stable within the
 * same period.
 */

import { useEffect, useState } from 'react'

export function useNow(seed: Date, periodMs = 60_000): Date {
  const [now, setNow] = useState<Date>(() => seed)

  // Bucket the seed to the period. Same-period renders see the same value
  // here and skip the effect; only when the server clock advances a full
  // period do we push a reseed.
  const seedBucket = Math.floor(seed.getTime() / Math.max(periodMs, 1000))

  useEffect(() => {
    // Reseed to the server's current time (within the bucket resolution).
    setNow(new Date(seedBucket * Math.max(periodMs, 1000)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedBucket, periodMs])

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), periodMs)
    return () => window.clearInterval(id)
  }, [periodMs])

  return now
}

export default useNow
